import { AdminEmailEditor } from '../../../components/admin/AdminEmailEditor';
import React, { useState } from 'react';
import {
  Alert,
  Button,
  FormControl,
  FormLabel,
  Input,
} from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailDrawer } from '../../../components/common/DetailDrawer';
import { AdminSequencesAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import { TemplateFolderSelect } from './TemplateFolders';
import { useTemplateFolders } from './useTemplateFolders';
import type { PlatformEmailTemplate } from '../../../types/admin';
import { MessagePreview, TemplateFieldGuide } from './MessagePersonalization';

/**
 * Write and edit the body of one onboarding email.
 *
 * Editing is explicit — nothing saves as you type — because these go to every
 * customer on the sequence and a half-finished sentence is not something to
 * discover after the fact.
 */
export const TemplateEditorDrawer: React.FC<{
  templateId: string | null;
  onClose: () => void;
}> = ({ templateId, onClose }) => {
  const queryClient = useQueryClient();
  const open = templateId !== null;
  const foldersQuery = useTemplateFolders(open);
  // The draft carries the id it belongs to, so opening a different template
  // discards it without an effect that resets state on every render pass.
  const [draftFor, setDraftFor] = useState<{
    id: string | null;
    values: Partial<PlatformEmailTemplate>;
  }>({ id: null, values: {} });
  const draft = draftFor.id === templateId ? draftFor.values : {};
  const setDraft = (
    update: (previous: Partial<PlatformEmailTemplate>) => Partial<PlatformEmailTemplate>
  ) => setDraftFor({ id: templateId, values: update(draft) });

  const templatesQuery = useQuery({
    queryKey: ['admin', 'email-templates'],
    queryFn: () => AdminSequencesAPI.listTemplates(),
    enabled: open,
  });

  const template = templatesQuery.data?.results.find((row) => row.id === templateId);

  const saveMutation = useMutation({
    mutationFn: (changes: Partial<PlatformEmailTemplate>) =>
      AdminSequencesAPI.updateTemplate(templateId as string, changes),
    onSuccess: () => {
      setDraftFor({ id: null, values: {} });
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'sequences'] });
    },
  });

  const field = <K extends keyof PlatformEmailTemplate>(key: K) =>
    (draft[key] ?? template?.[key] ?? '') as string;

  const dirty = Object.keys(draft).some(
    (key) => draft[key as keyof PlatformEmailTemplate] !== template?.[key as keyof PlatformEmailTemplate]
  );

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={template?.name ?? 'Template'}
      subtitle={template ? `Last edited ${new Date(template.updated_at).toLocaleString()}` : undefined}
      isLoading={templatesQuery.isLoading}
      error={templatesQuery.error as Error | null}
      width={640}
      actions={
        dirty ? (
          <Button size="sm" loading={saveMutation.isPending} onClick={() => saveMutation.mutate(draft)}>
            Save
          </Button>
        ) : undefined
      }
    >
      {template && (
        <>
          {saveMutation.isError && (
            <Alert color="danger" variant="soft">
              {getApiErrorMessage(saveMutation.error, 'That could not be saved.')}
            </Alert>
          )}

          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              size="sm"
              value={field('name')}
              onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
            />
          </FormControl>

          {foldersQuery.error && <Alert color="danger">Folders could not be loaded. <Button size="sm" onClick={() => foldersQuery.refetch()}>Retry</Button></Alert>}
          <FormControl>
            <FormLabel>Folder</FormLabel>
            <TemplateFolderSelect folders={foldersQuery.data ?? []}
              value={draft.folder !== undefined ? draft.folder : template.folder ?? null}
              disabled={!foldersQuery.data || saveMutation.isPending}
              onChange={folder => setDraft(prev => ({ ...prev, folder }))} />
          </FormControl>
          <FormControl>
            <FormLabel>Subject</FormLabel>
            <Input
              size="sm"
              value={field('subject')}
              onChange={(event) => setDraft((prev) => ({ ...prev, subject: event.target.value }))}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Body</FormLabel>
            <AdminEmailEditor label="Body" text={field('body_text')} html={field('body_html')}
              disabled={saveMutation.isPending}
              onChange={(body_html, body_text) => setDraft(prev => ({ ...prev, body_html, body_text }))} />
          </FormControl>

          <TemplateFieldGuide />
          <MessagePreview title={field('name') || 'Sequence email'} subject={field('subject')} body={field('body_text')} body_html={field('body_html')} />
        </>
      )}
    </DetailDrawer>
  );
};

export default TemplateEditorDrawer;
